import { Icon } from '@/app_components/common/Icon';
import { Image } from '@/app_components/common/Image';
import { Link } from '@/app_components/common/Link';
import { AccordionDescription } from '@/app_components/common/widgets/common/AccordionDescription';
import { Button } from '@/app_components/ui/Button';
import {
  type EducationWidgetFeatures,
  parseNestedFields,
  type Taxonomy,
  type WidgetBuilder,
} from '@/app_data/widgets/utils';
import { AnalyticsEvent, ContentPlacement } from '@/app_lib/types/analytics';
import { cn, wpImageUrlBuilder } from '@/app_lib/utils/utils';

interface Props {
  data: WidgetBuilder;
  position?: Taxonomy['position'];
  index?: number;
}

type Fields = 'title' | 'banner' | 'description' | 'subheading' | 'visit_website' | 'logo' | 'features';

export const EducationAdsCard = ({ position, data, index = 0 }: Props) => {
  const getByKeys = data.build<Fields>(index).get;
  const link = parseNestedFields<{
    href: string;
    blank: number;
    text: string;
  }>(getByKeys('visit_website'));
  const logo = parseNestedFields<{
    data: {
      // biome-ignore-start lint/style/useNamingConvention: <this is external data>
      image_id: string;
      image_src: string;
      // biome-ignore-end lint/style/useNamingConvention: <>
    };
  }>(getByKeys('logo'));

  const features = parseNestedFields<EducationWidgetFeatures>(getByKeys('features'));

  const logoUrl = wpImageUrlBuilder(logo?.data.image_src);

  const DefaultImageSize = {
    height: 88,
    width: 150,
  };

  return (
    <div className='@container transition-colors'>
      <div
        className={cn(
          '-z-2 isolate @md:my-4 bg-primary shadow-md font-graphik-compact',
          AnalyticsEvent.ImpressionElement,
        )}
        data-cy={ContentPlacement.EducationCard}
      >
        <div
          className={cn(
            'flex w-full items-center justify-center bg-accent @md:px-8 px-4 py-2 font-graphik font-bold text-white uppercase',
            AnalyticsEvent.ImpressionElement,
          )}
          data-cy={ContentPlacement.EducationCardBanner}
        >
          {getByKeys('banner')}
        </div>

        <div
          className={cn(
            'grid @md:grid-cols-[1fr_160px] grid-cols-1 @md:gap-8 @md:px-8 px-4 py-4 @md:pt-8 @md:pb-6',
            AnalyticsEvent.ImpressionElement,
          )}
          data-cy={ContentPlacement.EducationCardContainer}
        >
          <div
            className={cn('flex flex-col @md:gap-4', AnalyticsEvent.ImpressionElement)}
            data-cy={ContentPlacement.EducationCardContent}
          >
            <div
              className={cn(AnalyticsEvent.ImpressionElement)}
              data-cy={ContentPlacement.EducationCardTitle}
            >
              <span
                className={cn('typography-level-4 font-semibold mb-2', AnalyticsEvent.ImpressionElement)}
                data-cy={ContentPlacement.EducationCardTitleHeading}
              >
                {getByKeys('title')}
              </span>
              <span
                className={cn('typography-level-5 mb-2', AnalyticsEvent.ImpressionElement)}
                data-cy={ContentPlacement.EducationCardTitleSubheading}
              >
                {getByKeys('subheading')}
              </span>
              <div
                className={cn('typography-sm mb-2', AnalyticsEvent.ImpressionElement)}
                data-cy={ContentPlacement.EducationCardContentDescription}
              >
                <AccordionDescription content={getByKeys('description') || ''} />
              </div>

              {link && (
                <Button
                  asChild
                  className='mt-auto mb-2 flex @md:hidden w-full uppercase bg-inverse group'
                  variant='primary'
                >
                  <Link
                    href={link.href}
                    target={link.blank === 1 ? '_blank' : undefined}
                  >
                    {link.text || 'Visit Website'}
                    <Icon icon='ChevronRight' />
                  </Link>
                </Button>
              )}
            </div>
          </div>

          <div
            className={cn('@md:col-start-2 row-start-1 flex h-full flex-col', AnalyticsEvent.ImpressionElement)}
            data-cy={ContentPlacement.EducationCardAction}
          >
            <div
              className={`@md:row-start-auto row-start-1 @md:mb-2 mb-2 aspect-video @md:max-w-full max-w-[150px] @md:aspect-[${DefaultImageSize.width}/${DefaultImageSize.height}] flex items-center justify-center`}
            >
              <Image
                alt={getByKeys('title') || ''}
                className={`aspect-video @md:aspect-[${DefaultImageSize.width}/${DefaultImageSize.height}] object-contain`}
                data-cy={ContentPlacement.EducationCardPicture}
                height={DefaultImageSize.height}
                src={logoUrl}
                width={DefaultImageSize.width}
              />
            </div>
            {link && (
              <Button
                asChild
                className='mt-auto @md:flex hidden w-full uppercase bg-inverse group'
                variant='primary'
              >
                <Link
                  href={link.href}
                  target={link.blank === 1 ? '_blank' : undefined}
                >
                  {link.text || 'Visit Website'}
                  <Icon icon='ChevronRight' />
                </Link>
              </Button>
            )}
          </div>
        </div>

        {features && (
          <div className='@md:px-8 px-4 @md:pb-8 pb-4'>
            <ul className='flex flex-col gap-2'>
              {Array.from({ length: features.rows }, (_rowIterable, row) => {
                const featureKey = `row_${row}`;
                const featureRowData = features.data[featureKey] || [];
                // Use a hash of the row data for a stable key
                const rowKey = `${position}-${featureRowData.join('-')}`;
                return (
                  <li
                    className='group flex items-center justify-between stroke-secondary border-b pb-2'
                    key={rowKey}
                  >
                    {Array.from({ length: features.columns }, (_colIterable, col) => {
                      const colKey = `${position}-${featureRowData[col] || ''}-${col}`;
                      return (
                        <span
                          className='group flex items-center gap-2 font-semibold text-secondary last:cursor-pointer last:text-accent'
                          key={colKey}
                        >
                          {featureRowData[col] || ''}
                        </span>
                      );
                    })}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
